/* Album addon. One canvas renderer for previews, 3D and print files.
   Sources: without a configured proxy, the customer's browser queries Deezer (JSONP) and,
   when Deezer fails or finds nothing, the iTunes Search API, both free and keyless. Each
   customer uses their own IP: a server IP sending every search gets blocked by Deezer.
   With ALBUM_POSTER_CONFIG.endpoint, the MusicBrainz proxy is used instead (local tests). */
(function () {
  'use strict';
  if (window.AlbumPoster) return;
  var FORMATS = {
    '10x15': { width: 1181, height: 1772, mmWidth: 100, mmHeight: 150 },
    'A4': { width: 2480, height: 3508, mmWidth: 210, mmHeight: 297 }
  };
  var images = new Map();
  var codes = new Map();
  var FALLBACK_FONT = 'Arial, Helvetica, sans-serif';
  // Raleway, self-hosted and declared in album-poster.css.
  var DEFAULT_FONT = 'Album Poster Raleway';
  var FONT = FALLBACK_FONT;
  // Poster typeface: data.font, else ALBUM_POSTER_CONFIG.font, else Raleway.
  function fontFamily(name) {
    return String(name || (window.ALBUM_POSTER_CONFIG || {}).font || DEFAULT_FONT).replace(/["\\]/g, '').trim();
  }
  function useFont(name) {
    var family = fontFamily(name);
    FONT = family ? '"' + family + '", ' + FALLBACK_FONT : FALLBACK_FONT;
  }
  // Canvas silently falls back to Arial when a web font is not loaded yet: wait for
  // both weights (5 s at most, then draw anyway). The text sample covers French accents.
  var fontLoads = new Map();
  function fontReady(name) {
    var family = fontFamily(name);
    if (!document.fonts || !family) return Promise.resolve();
    if (!fontLoads.has(family)) {
      var loads = Promise.all(['500', '700'].map(function (weight) { return document.fonts.load(weight + ' 24px "' + family + '"', 'Aéè1'); }));
      fontLoads.set(family, Promise.race([loads, new Promise(function (resolve) { setTimeout(resolve, 5000); })]).catch(function () {}));
    }
    return fontLoads.get(family);
  }
  function formatLabel(value) { return value === 'A4' ? '21 × 29,7 cm' : '10 × 15 cm'; }
  function formatValue(value) {
    if (FORMATS[value]) return value;
    var label = String(value || '').toLowerCase().replace(/\s|cm/g, '').replace('×', 'x').replace('.', ',');
    return label === '21x29,7' ? 'A4' : label === '10x15' ? '10x15' : null;
  }

  function endpoint() {
    return String((window.ALBUM_POSTER_CONFIG || {}).endpoint || '').replace(/\/+$/, '');
  }
  function apiURL(path) {
    if (!endpoint()) throw new Error('La recherche d’albums est momentanément indisponible.');
    return endpoint() + path;
  }
  // ---- Browser sources (no proxy, no key) ----
  var SEARCH_DOWN = 'La recherche d’albums ne répond pas pour le moment. Réessayez dans quelques instants.';
  function sourceError(message) { var error = new Error(message); error.source = true; return error; }
  function deezerIdFromURL(value) {
    try {
      var url = new URL(value);
      if (url.protocol !== 'https:' || !/^(?:www\.)?deezer\.com$/.test(url.hostname)) return null;
      return (/^\/(?:[a-z]{2}\/)?album\/(\d{1,15})\/?$/.exec(url.pathname) || [])[1] || null;
    } catch (_) { return null; }
  }
  // Albums without artwork point to an empty md5 (".../images/cover//1000x1000-...").
  function deezerCover(md5, size) {
    return /^[0-9a-f]{32}$/.test(md5 || '') ? 'https://cdn-images.dzcdn.net/images/cover/' + md5 + '/' + size + 'x' + size + '-000000-80-0-0.jpg' : null;
  }
  // iTunes artwork URLs end with the requested size: ask for 3000 px, the largest that exists.
  function itunesArtwork(url, size) {
    try {
      var parsed = new URL(url);
      if (parsed.protocol !== 'https:' || !/^is\d+-ssl\.mzstatic\.com$/.test(parsed.hostname)) return null;
      return /\/\d+x\d+bb\.(?:jpg|png)$/.test(parsed.pathname) ? url.replace(/\/\d+x\d+bb\.(?:jpg|png)$/, '/' + size + 'x' + size + 'bb.jpg') : null;
    } catch (_) { return null; }
  }
  // Covers are drawn straight from these CDNs (CORS allowed), never from another host.
  function trustedCover(url) {
    try {
      var parsed = new URL(url);
      return parsed.protocol === 'https:' && (parsed.hostname === 'cdn-images.dzcdn.net' || /^is\d+-ssl\.mzstatic\.com$/.test(parsed.hostname));
    } catch (_) { return false; }
  }
  function textKey(value) { return String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); }
  // The same album often exists twice (explicit/clean, territories): keep the most relevant.
  function uniqueAlbums(items) {
    var seen = new Set();
    return items.filter(function (item) {
      var identity = [textKey(item.artists.map(function (a) { return a.name; }).join(' ')), textKey(item.name), item.total_tracks].join('|');
      if (seen.has(identity)) return false;
      seen.add(identity); return true;
    });
  }
  function deezerSearchItems(results) {
    return uniqueAlbums((results || []).filter(function (summary) {
      // A single gives a one-line tracklist: keep albums, EPs and compilations.
      return summary.record_type !== 'single' && deezerCover(summary.md5_image, 250);
    }).map(function (summary) {
      return {
        id: 'deezer-' + summary.id, name: summary.title,
        artists: [{ name: summary.artist && summary.artist.name }].filter(function (a) { return a.name; }),
        // Search results carry no release date; the year is shown once the album is chosen.
        release_date: '', total_tracks: summary.nb_tracks, track_hint: '',
        images: [{ url: deezerCover(summary.md5_image, 250) }]
      };
    }));
  }
  function deezerToAlbum(album, tracks) {
    var items = (tracks || []).slice().sort(function (a, b) { return a.disk_number - b.disk_number || a.track_position - b.track_position; });
    if (!items.length || items.length !== album.nb_tracks) throw sourceError('La tracklist de cet album est incomplète. Choisissez une autre édition.');
    if (items.some(function (t) { return !t.title || !(t.duration > 0); })) throw sourceError('La durée de certaines pistes est absente. Choisissez une autre édition.');
    var cover = deezerCover(album.md5_image, 1000);
    if (!cover || !album.release_date) throw sourceError('La pochette ou la date de sortie est absente pour cet album.');
    return {
      id: 'deezer-' + album.id, source_url: album.link || '', name: album.title,
      artists: [{ name: album.artist && album.artist.name }].filter(function (a) { return a.name; }),
      images: [{ url: cover, width: 1000 }],
      release_date: album.release_date, release_date_precision: 'day', total_tracks: items.length,
      tracks: { items: items.map(function (t) { return { name: t.title, duration_ms: t.duration * 1000 }; }), next: null },
      // Printed as an EAN-13 barcode in place of a Spotify Code.
      barcode: album.upc || ''
    };
  }
  function itunesSearchItems(results) {
    return uniqueAlbums((results || []).filter(function (album) {
      return album.wrapperType === 'collection' && !/ - Single$/.test(album.collectionName || '') && album.trackCount > 1 && itunesArtwork(album.artworkUrl100, 250);
    }).map(function (album) {
      return {
        id: 'itunes-' + album.collectionId, name: album.collectionName,
        artists: [{ name: album.artistName }].filter(function (a) { return a.name; }),
        release_date: '', total_tracks: album.trackCount, track_hint: '',
        images: [{ url: itunesArtwork(album.artworkUrl100, 250) }]
      };
    }));
  }
  function itunesToAlbum(results) {
    var album = (results || []).filter(function (r) { return r.wrapperType === 'collection'; })[0];
    if (!album) throw sourceError('Cet album est introuvable. Choisissez-en un autre.');
    var items = results.filter(function (r) { return r.wrapperType === 'track'; })
      .sort(function (a, b) { return a.discNumber - b.discNumber || a.trackNumber - b.trackNumber; });
    // iTunes counts items it does not list (digital booklet, unavailable bonus at the end):
    // accept numbering without gaps on every disc, refuse a missing track in the middle.
    var gap = items.some(function (t, i) {
      var previous = items[i - 1];
      return t.trackNumber !== (previous && previous.discNumber === t.discNumber ? previous.trackNumber + 1 : 1);
    });
    if (!items.length || gap || items.length > album.trackCount) throw sourceError('La tracklist de cet album est incomplète. Choisissez une autre édition.');
    if (items.some(function (t) { return !t.trackName || !(t.trackTimeMillis > 0); })) throw sourceError('La durée de certaines pistes est absente. Choisissez une autre édition.');
    var cover = itunesArtwork(album.artworkUrl100, 3000);
    var date = /^\d{4}-\d{2}-\d{2}/.exec(album.releaseDate || '');
    if (!cover || !date) throw sourceError('La pochette ou la date de sortie est absente pour cet album.');
    return {
      id: 'itunes-' + album.collectionId, source_url: album.collectionViewUrl || '', name: album.collectionName,
      artists: [{ name: album.artistName }].filter(function (a) { return a.name; }),
      images: [{ url: cover, width: 3000 }],
      release_date: date[0], release_date_precision: 'day', total_tracks: items.length,
      tracks: { items: items.map(function (t) { return { name: t.trackName, duration_ms: t.trackTimeMillis }; }), next: null },
      barcode: ''
    };
  }
  // Deezer's API sends no CORS headers: JSONP from the customer's browser. The answer is
  // only read as data, then validated like any other source.
  var jsonpSequence = 0;
  function deezer(path, parameters, signal) {
    return new Promise(function (resolve, reject) {
      if (signal && signal.aborted) return reject(new DOMException('Aborted', 'AbortError'));
      var name = '__albumPosterDeezer' + Date.now().toString(36) + (jsonpSequence++);
      var url = new URL(path, 'https://api.deezer.com/');
      url.search = new URLSearchParams(Object.assign({}, parameters, { output: 'jsonp', callback: name }));
      var script = document.createElement('script');
      var timer = setTimeout(function () { finish(sourceError(SEARCH_DOWN)); }, 8000);
      function onAbort() { finish(new DOMException('Aborted', 'AbortError')); }
      function finish(error, data) {
        clearTimeout(timer); script.remove();
        if (signal) signal.removeEventListener('abort', onAbort);
        // A late answer must not throw: keep a no-op callback for a minute.
        window[name] = function () {};
        setTimeout(function () { try { delete window[name]; } catch (_) { window[name] = undefined; } }, 60000);
        if (error) reject(error); else resolve(data);
      }
      window[name] = function (data) {
        if (!data || typeof data !== 'object') return finish(sourceError(SEARCH_DOWN));
        if (data.error) return finish(sourceError(data.error.code === 800 ? 'Cet album est introuvable sur Deezer.' : SEARCH_DOWN));
        finish(null, data);
      };
      script.onerror = function () { finish(sourceError(SEARCH_DOWN)); };
      if (signal) signal.addEventListener('abort', onAbort);
      script.referrerPolicy = 'no-referrer';
      script.src = url.href;
      document.head.appendChild(script);
    });
  }
  async function itunes(path, parameters, signal) {
    var url = new URL(path, 'https://itunes.apple.com/');
    url.search = new URLSearchParams(Object.assign({ country: 'FR' }, parameters));
    var response;
    try { response = await fetch(url.href, { signal: signal, credentials: 'omit', referrerPolicy: 'no-referrer' }); }
    catch (error) { if (error.name === 'AbortError') throw error; throw sourceError(SEARCH_DOWN); }
    if (!response.ok) throw sourceError(SEARCH_DOWN);
    var data = await response.json().catch(function () { return null; });
    if (!data || !Array.isArray(data.results)) throw sourceError(SEARCH_DOWN);
    return data.results;
  }
  // Deezer first (best catalogue, 1000 px covers, barcode); iTunes when Deezer fails or
  // finds nothing. Both failing is the only error shown.
  async function browserSearch(value, signal) {
    var items = [], deezerFailed = false;
    try { items = deezerSearchItems((await deezer('search/album', { q: value, limit: '25' }, signal)).data).slice(0, 12); }
    catch (error) { if (error.name === 'AbortError') throw error; deezerFailed = true; }
    if (items.length) return { albums: { items: items } };
    try { items = itunesSearchItems(await itunes('search', { term: value, entity: 'album', limit: '25' }, signal)).slice(0, 12); }
    catch (error) { if (error.name === 'AbortError' || !deezerFailed) throw error; throw sourceError(SEARCH_DOWN); }
    // iTunes only lists albums sold as downloads: with Deezer down, "nothing found" would
    // be wrong for many albums (Racine carrée, Ipséité), so say the search is unavailable.
    if (!items.length && deezerFailed) throw sourceError(SEARCH_DOWN);
    return { albums: { items: items } };
  }
  async function browserAlbum(id, signal) {
    var match = /^(deezer|itunes)-(\d{1,15})$/.exec(id);
    if (!match) throw sourceError('Cet album est introuvable. Choisissez-en un autre.');
    if (match[1] === 'itunes') return itunesToAlbum(await itunes('lookup', { id: match[2], entity: 'song', limit: '200' }, signal));
    // /album truncates its embedded tracklist at 25 titles; /tracks returns all of them.
    var both = await Promise.all([deezer('album/' + match[2], {}, signal), deezer('album/' + match[2] + '/tracks', { limit: '500' }, signal)]);
    return deezerToAlbum(both[0], both[1].data);
  }
  function browserMode() { return !endpoint(); }
  function searchAlbums(value, signal) {
    return browserMode() ? browserSearch(value, signal) : request('/search?q=' + encodeURIComponent(value), signal);
  }
  async function fetchAlbum(id, signal) {
    return normalize(browserMode() ? await browserAlbum(id, signal) : await request('/albums/' + encodeURIComponent(id), signal));
  }
  // Images: straight from the Deezer/iTunes CDN in browser mode, else through the proxy.
  function imageSource(url) {
    if (browserMode()) { if (!trustedCover(url)) throw new Error('Pochette invalide.'); return url; }
    return apiURL('/image?url=' + encodeURIComponent(url));
  }
  function albumId(value) {
    var text = String(value || '').trim();
    var uri = /^spotify:album:([A-Za-z0-9]{22})$/.exec(text);
    if (uri) return uri[1];
    try {
      var url = new URL(text);
      if (url.protocol !== 'https:' || url.hostname !== 'open.spotify.com') return null;
      var match = /^\/(?:intl-[a-z]{2}\/)?album\/([A-Za-z0-9]{22})\/?$/.exec(url.pathname);
      return match ? match[1] : null;
    } catch (_) { return null; }
  }
  async function request(path, signal) {
    var response = await fetch(apiURL(path), { signal: signal });
    var data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Impossible de charger cet album. Réessayez.');
    return data;
  }
  function normalize(raw) {
    if (!raw || !/^(?:[A-Za-z0-9]{22}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|(?:deezer|itunes)-\d{1,15})$/i.test(raw.id || '')) throw new Error('Album invalide.');
    var tracks = (raw.tracks && raw.tracks.items) || [];
    var covers = (raw.images || []).slice().sort(function (a, b) { return (b.width || 0) - (a.width || 0); });
    if (!raw.name || !(raw.artists || []).some(function (artist) { return artist.name; }) ||
        !covers.length || !tracks.length || !raw.release_date ||
        tracks.length !== raw.total_tracks || raw.tracks.next ||
        tracks.some(function (t) { return !t.name || !Number.isFinite(t.duration_ms) || t.duration_ms < 0; })) {
      throw new Error('Les informations de cet album sont incomplètes. Choisissez une autre édition.');
    }
    var spotifyId = raw.spotify_id || (/^[A-Za-z0-9]{22}$/.test(raw.id) ? raw.id : null);
    return {
      id: raw.id,
      spotifyId: spotifyId,
      barcode: ean13(raw.barcode),
      sourceURL: raw.source_url || '',
      title: raw.name,
      artist: (raw.artists || []).map(function (a) { return a.name; }).join(', '),
      cover: covers[0].url,
      releaseDate: raw.release_date,
      releasePrecision: raw.release_date_precision,
      tracks: tracks.map(function (t, i) { return { name: t.name, number: i + 1, durationMs: t.duration_ms }; }),
      durationMs: tracks.reduce(function (total, t) { return total + t.duration_ms; }, 0),
      url: spotifyId ? 'https://open.spotify.com/album/' + spotifyId : ''
    };
  }
  function duration(ms) {
    var seconds = Math.floor(ms / 1000);
    return Math.floor(seconds / 60) + 'min ' + String(seconds % 60).padStart(2, '0') + 's';
  }
  function releaseDate(value) {
    var parts = String(value || '').split('-');
    return parts.length === 3 ? parts.reverse().join('.') : parts.length === 2 ? parts[1] + '.' + parts[0] : value;
  }
  // `photo`: the customer's own square photo, drawn in place of the album artwork.
  // `photoKey` lets JSON signatures (3D scene) tell two photos apart.
  function dataFromDraft(block, draft) {
    var meta = (draft.__meta || {})[block.stateKey || 'Album'] || {};
    var photo = typeof File !== 'undefined' && draft.Photo instanceof File ? draft.Photo : null;
    return {
      album: meta.album || null, format: formatValue(draft.Format) || '10x15',
      photo: photo, photoKey: photo ? [photo.name, photo.size, photo.lastModified].join(':') : ''
    };
  }
  function loadImage(src, timeout, label) {
    label = label || 'la pochette';
    if (images.has(src)) return images.get(src);
    var promise = new Promise(function (resolve, reject) {
      var img = new Image();
      var timer = setTimeout(function () { img.src = ''; reject(new Error('Le chargement de ' + label + ' a pris trop de temps. Réessayez.')); }, timeout || 25000);
      img.crossOrigin = 'anonymous';
      img.onload = function () { clearTimeout(timer); resolve(img); };
      img.onerror = function () { clearTimeout(timer); reject(new Error('Impossible de charger ' + label + '. Réessayez.')); };
      img.src = src;
    });
    images.set(src, promise);
    if (images.size > 24) images.delete(images.keys().next().value);
    promise.catch(function () { images.delete(src); });
    return promise;
  }
  var photos = new WeakMap();
  function loadPhoto(file) {
    if (!photos.has(file)) photos.set(file, loadImage(URL.createObjectURL(file), 15000, 'votre photo'));
    return photos.get(file);
  }
  async function resources(album, photo) {
    if (album.spotifyId && !browserMode() && !codes.has(album.spotifyId)) {
      // A missing/unavailable code is optional. Cache that result for this page session.
      codes.set(album.spotifyId, loadImage(apiURL('/code?id=' + encodeURIComponent(album.spotifyId)), 8000, 'le code Spotify').catch(function () { return null; }));
      if (codes.size > 24) codes.delete(codes.keys().next().value);
    }
    return Promise.all([
      photo ? loadPhoto(photo) : loadImage(imageSource(album.cover)),
      album.spotifyId && !browserMode() ? codes.get(album.spotifyId) : Promise.resolve(null)
    ]);
  }
  // Main colours of the artwork: 8 levels per channel, most frequent first, skipping
  // colours too close to one already kept (threshold relaxed for near-monochrome covers).
  // Square artwork without a scanned sleeve frame (A7 by SCH: thin white border, scanned
  // slightly askew). A frame is confirmed only when all four outer lines are uniform and of
  // a similar colour; it is then trimmed as long as a line still holds 15 % of frame pixels
  // (the askew edge), on every side by the deepest side, 8 % at most. Plain backgrounds on
  // one side only are therefore kept.
  var artworkBoxes = new WeakMap();
  function artworkBox(img) {
    if (artworkBoxes.has(img)) return artworkBoxes.get(img);
    var side = Math.min(img.naturalWidth, img.naturalHeight);
    var box = { x: (img.naturalWidth - side) / 2, y: (img.naturalHeight - side) / 2, size: side };
    var N = 200, sample = document.createElement('canvas');
    sample.width = N; sample.height = N;
    var ctx = sample.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, box.x, box.y, side, side, 0, 0, N, N);
    var data = ctx.getImageData(0, 0, N, N).data;
    function pixel(x, y) { var i = (y * N + x) * 4; return [data[i], data[i + 1], data[i + 2]]; }
    function near(p, color) { return Math.abs(p[0] - color[0]) < 36 && Math.abs(p[1] - color[1]) < 36 && Math.abs(p[2] - color[2]) < 36; }
    function share(points, color) { return points.filter(function (p) { return near(p, color); }).length / points.length; }
    var limit = Math.floor(N * 0.08), sides = [
      function (k) { var line = []; for (var x = 0; x < N; x++) line.push(pixel(x, k)); return line; },
      function (k) { var line = []; for (var x = 0; x < N; x++) line.push(pixel(x, N - 1 - k)); return line; },
      function (k) { var line = []; for (var y = 0; y < N; y++) line.push(pixel(k, y)); return line; },
      function (k) { var line = []; for (var y = 0; y < N; y++) line.push(pixel(N - 1 - k, y)); return line; }
    ];
    var colors = sides.map(function (line) {
      var points = line(0), mean = [0, 0, 0];
      points.forEach(function (p) { mean[0] += p[0]; mean[1] += p[1]; mean[2] += p[2]; });
      mean = mean.map(function (v) { return v / points.length; });
      return share(points, mean) >= 0.9 ? mean : null;
    });
    var framed = colors.every(function (c) { return c && Math.hypot(c[0] - colors[0][0], c[1] - colors[0][1], c[2] - colors[0][2]) < 60; });
    if (framed) {
      var depth = Math.max.apply(null, sides.map(function (line, index) {
        var k = 0;
        while (k < limit && share(line(k), colors[index]) >= 0.15) k++;
        return k;
      }));
      // One more sample row absorbs the blurred edge between the frame and the artwork.
      var trim = Math.min(limit, depth + 1) * side / N;
      box = { x: box.x + trim, y: box.y + trim, size: side - 2 * trim };
    }
    artworkBoxes.set(img, box);
    return box;
  }
  function palette(img, count) {
    var sample = document.createElement('canvas');
    sample.width = 48; sample.height = 48;
    var ctx = sample.getContext('2d', { willReadFrequently: true });
    var box = artworkBox(img);
    ctx.drawImage(img, box.x, box.y, box.size, box.size, 0, 0, 48, 48);
    var pixels = ctx.getImageData(0, 0, 48, 48).data, buckets = new Map();
    for (var i = 0; i < pixels.length; i += 4) {
      var key = (pixels[i] >> 5) << 6 | (pixels[i + 1] >> 5) << 3 | (pixels[i + 2] >> 5);
      var bucket = buckets.get(key) || [0, 0, 0, 0];
      bucket[0] += pixels[i]; bucket[1] += pixels[i + 1]; bucket[2] += pixels[i + 2]; bucket[3]++;
      buckets.set(key, bucket);
    }
    var colors = Array.from(buckets.values()).sort(function (a, b) { return b[3] - a[3]; })
      .map(function (b) { return [Math.round(b[0] / b[3]), Math.round(b[1] / b[3]), Math.round(b[2] / b[3])]; });
    var chosen = [];
    [80, 50, 25, 0].forEach(function (threshold) {
      colors.forEach(function (color) {
        if (chosen.length < count && chosen.indexOf(color) === -1 && chosen.every(function (c) {
          return Math.hypot(c[0] - color[0], c[1] - color[1], c[2] - color[2]) > threshold;
        })) chosen.push(color);
      });
    });
    return chosen;
  }
  // Poster background, so that the lower part reads as a continuation of the artwork:
  // the whole artwork, blurred until a face or figure can no longer be recognised
  // (3 colour columns enlarged step by step), a light veil of its dominant colour to keep
  // the text calm, and a fine photographic grain. The sharp artwork fades into it (35 %).
  function enlarge(step, W, H) {
    while (step.width * 2 < W / 4) {
      var next = document.createElement('canvas'); next.width = step.width * 2; next.height = step.height * 2;
      var nextCtx = next.getContext('2d'); nextCtx.imageSmoothingEnabled = true; nextCtx.imageSmoothingQuality = 'high';
      nextCtx.drawImage(step, 0, 0, next.width, next.height);
      step = next;
    }
    return step;
  }
  var grainTile = null;
  function grain() {
    if (grainTile) return grainTile;
    grainTile = document.createElement('canvas'); grainTile.width = 160; grainTile.height = 160;
    var tileCtx = grainTile.getContext('2d'), noise = tileCtx.createImageData(160, 160);
    // Deterministic noise (same print file every time).
    for (var i = 0, seed = 7; i < noise.data.length; i += 4) {
      seed = (seed * 16807) % 2147483647;
      var v = seed % 256;
      noise.data[i] = noise.data[i + 1] = noise.data[i + 2] = v; noise.data[i + 3] = 14;
    }
    tileCtx.putImageData(noise, 0, 0);
    return grainTile;
  }
  function posterBackground(img) {
    var box = artworkBox(img), dominant = palette(img, 1)[0];
    return { fade: 0.35, paint: function (ctx, W, H) {
      ctx.fillStyle = 'rgb(' + dominant.join(',') + ')'; ctx.fillRect(0, 0, W, H);
      var cols = 3, rows = Math.max(2, Math.round(cols * H / W)), crop = box.size * (W / H);
      var step = document.createElement('canvas'); step.width = cols; step.height = rows;
      var stepCtx = step.getContext('2d'); stepCtx.imageSmoothingEnabled = true; stepCtx.imageSmoothingQuality = 'high';
      stepCtx.drawImage(img, box.x + (box.size - crop) / 2, box.y, crop, box.size, 0, 0, cols, rows);
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(enlarge(step, W, H), 0, 0, W, H);
      ctx.fillStyle = 'rgba(' + dominant.join(',') + ',.3)'; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = ctx.createPattern(grain(), 'repeat'); ctx.fillRect(0, 0, W, H);
    } };
  }
  function averageArea(canvas, x, y, width, height) {
    var sample = document.createElement('canvas'); sample.width = 16; sample.height = 16;
    var ctx = sample.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(canvas, x, y, width, height, 0, 0, 16, 16);
    var pixels = ctx.getImageData(0, 0, 16, 16).data, sums = [0, 0, 0];
    for (var i = 0; i < pixels.length; i += 4) for (var c = 0; c < 3; c++) sums[c] += pixels[i + c];
    return sums.map(function (n) { return Math.round(n / 256); });
  }
  function textColor(rgb) {
    var values = rgb.map(function (n) { n /= 255; return n <= 0.04045 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4); });
    var light = values[0] * 0.2126 + values[1] * 0.7152 + values[2] * 0.0722 < 0.24;
    // Mid-tone pink/purple backgrounds keep a light treatment; pastel backgrounds use
    // dark text. Both inherit a visible tint from the artwork instead of pure B/W.
    var base = light ? 255 : 16, pigment = 0.30;
    var ink = rgb.map(function (channel) { return Math.round(base * (1 - pigment) + channel * pigment); });
    // Keep tinted light ink luminous: strengthen the hue without turning it grey.
    var lift = light ? 250 - Math.max.apply(null, ink) : 0;
    return ink.map(function (channel) { return channel + lift; });
  }
  function transparentCode(image, rgb) {
    var canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth; canvas.height = image.naturalHeight;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0);
    var pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (var i = 0; i < pixels.data.length; i += 4) {
      // Preserve every official bar/logo pixel and its antialiasing; white becomes
      // transparent, the black motif takes the same tinted ink as the poster text.
      var alpha = 255 - pixels.data[i];
      pixels.data[i] = rgb[0]; pixels.data[i + 1] = rgb[1]; pixels.data[i + 2] = rgb[2];
      pixels.data[i + 3] = Math.round(pixels.data[i + 3] * alpha / 255);
    }
    ctx.putImageData(pixels, 0, 0);
    return canvas;
  }
  function wrap(ctx, text, width) {
    var lines = [], line = '';
    String(text).split(/\s+/).forEach(function (word) {
      if (line && ctx.measureText(line + word).width > width) { lines.push(line.trim()); line = ''; }
      // Very long unbroken titles still remain inside their column.
      for (var char of word) {
        if (ctx.measureText(line + char).width > width && line) { lines.push(line); line = ''; }
        line += char;
      }
      line += ' ';
    });
    if (line.trim()) lines.push(line.trim());
    return lines;
  }
  function fitHeading(ctx, text, width, size, maxLines) {
    var lines;
    do {
      ctx.font = '700 ' + size + 'px ' + FONT;
      lines = wrap(ctx, text, width);
      if (lines.length <= maxLines) return { size: size, lines: lines };
      size -= 1;
    } while (size >= 18);
    throw new Error('Le titre de cet album est trop long pour ce format.');
  }
  // A title wider than its column continues on a second line, aligned after its
  // number. Null when it would need more lines or a single word does not fit.
  function wrapTrack(ctx, track, width, maxLines) {
    var prefix = track.number + '. ', text = prefix + track.name;
    if (ctx.measureText(text).width <= width) return { text: text, lines: [{ text: text, dx: 0 }] };
    var indent = ctx.measureText(prefix).width, lines = [], line = prefix;
    var words = String(track.name).split(/\s+/).filter(Boolean);
    for (var i = 0; i < words.length; i++) {
      var room = lines.length ? width - indent : width;
      var next = line + (line === prefix || !line ? '' : ' ') + words[i];
      if (ctx.measureText(next).width <= room) { line = next; continue; }
      if (line === prefix || !line) return null;
      lines.push(line); line = words[i];
      if (ctx.measureText(line).width > width - indent) return null;
    }
    lines.push(line);
    if (lines.length > maxLines) return null;
    return { text: text, lines: lines.map(function (value, index) { return { text: value, dx: index ? indent : 0 }; }) };
  }
  // Titles stay whole within a column; columns are balanced by number of lines.
  function placeColumns(blocks, columns, budget) {
    var total = blocks.reduce(function (sum, block) { return sum + block.lines.length; }, 0);
    for (var target = Math.ceil(total / columns); target <= budget; target++) {
      var column = 0, used = 0, placed = [];
      for (var i = 0; i < blocks.length && column < columns; i++) {
        var count = blocks[i].lines.length;
        if (count > target) { column = columns; break; }
        if (used + count > target) { column++; used = 0; }
        placed.push({ column: column, row: used }); used += count;
      }
      if (column < columns && placed.length === blocks.length) return placed;
    }
    return null;
  }
  function trackLayout(ctx, tracks, width, height) {
    var preferred = tracks.length > 30 ? 4 : tracks.length > 14 ? 3 : tracks.length > 7 ? 2 : 1;
    var lineHeight = tracks.length > 30 ? 1.12 : 1.3, gap = 26, candidates = [];
    // For each column count, the largest single size that fits: never condensed text.
    for (var columns = preferred; columns <= 4; columns++) {
      var columnWidth = (width - (columns - 1) * gap) / columns;
      for (var size = 22; size >= 10; size--) {
        ctx.font = '500 ' + size + 'px ' + FONT;
        var blocks = [];
        for (var i = 0; i < tracks.length; i++) {
          var block = wrapTrack(ctx, tracks[i], columnWidth, 2);
          if (!block) break;
          blocks.push(block);
        }
        var placed = blocks.length === tracks.length && placeColumns(blocks, columns, Math.floor(height / (size * lineHeight)));
        if (!placed) continue;
        candidates.push({ size: size, columnWidth: columnWidth, blocks: blocks, placed: placed,
          wrapped: blocks.filter(function (b) { return b.lines.length > 1; }).length });
        break;
      }
    }
    if (!candidates.length) throw new Error('Cet album contient trop de titres pour une impression lisible dans ce format.');
    // Largest text first, but fewer two-line titles win when they keep 85 % of that size.
    var largest = Math.max.apply(null, candidates.map(function (c) { return c.size; }));
    var best = candidates.filter(function (c) { return c.size >= largest * 0.85; }).sort(function (a, b) { return a.wrapped - b.wrapped || b.size - a.size; })[0];
    return { columns: Math.max.apply(null, best.placed.map(function (p) { return p.column; })) + 1, size: best.size, wrapped: best.wrapped,
      items: best.blocks.map(function (block, index) {
        return { text: block.text, size: best.size, width: best.columnWidth, lines: block.lines,
          x: best.placed[index].column * (best.columnWidth + gap), y: best.placed[index].row * best.size * lineHeight, lineHeight: best.size * lineHeight };
      }) };
  }
  // EAN-13 (a 12-digit UPC-A gets its leading 0). Null when the check digit is wrong.
  function ean13(code) {
    var digits = String(code || '').replace(/\D/g, '');
    if (digits.length === 12) digits = '0' + digits;
    if (digits.length !== 13) return null;
    var sum = 0;
    for (var i = 0; i < 12; i++) sum += Number(digits[i]) * (i % 2 ? 3 : 1);
    return (10 - sum % 10) % 10 === Number(digits[12]) ? digits : null;
  }
  var EAN_L = ['0001101', '0011001', '0010011', '0111101', '0100011', '0110001', '0101111', '0111011', '0110111', '0001011'];
  var EAN_G = ['0100111', '0110011', '0011011', '0100001', '0011101', '0111001', '0000101', '0010001', '0001001', '0010111'];
  var EAN_R = ['1110010', '1100110', '1101100', '1000010', '1011100', '1001110', '1010000', '1000100', '1001000', '1110100'];
  var EAN_PARITY = ['LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG', 'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL'];
  function eanModules(digits) {
    var parity = EAN_PARITY[digits[0]], modules = '101';
    for (var i = 1; i <= 6; i++) modules += (parity[i - 1] === 'L' ? EAN_L : EAN_G)[digits[i]];
    modules += '01010';
    for (var j = 7; j <= 12; j++) modules += EAN_R[digits[j]];
    return modules + '101';
  }
  // Standard EAN-13 drawing, right-aligned on `right`: guard bars run longer, digits underneath.
  function drawBarcode(ctx, digits, right, top, height, module) {
    var modules = eanModules(digits), lead = module * 9;
    var left = right - modules.length * module, textSize = Math.round(module * 7);
    for (var i = 0; i < modules.length; i++) {
      if (modules[i] !== '1') continue;
      var guard = i < 3 || (i >= 45 && i < 50) || i >= 92;
      ctx.fillRect(left + i * module, top, module, height - (guard ? 0 : textSize));
    }
    ctx.font = '500 ' + textSize + 'px ' + FONT;
    ctx.textAlign = 'center';
    var textTop = top + height - textSize * 0.7;
    ctx.fillText(digits[0], left - lead / 2, textTop);
    for (var d = 0; d < 6; d++) ctx.fillText(digits[1 + d], left + (3 + d * 7 + 3.5) * module, textTop);
    for (var e = 0; e < 6; e++) ctx.fillText(digits[7 + e], left + (50 + e * 7 + 3.5) * module, textTop);
    ctx.textAlign = 'left';
  }
  async function draw(canvas, data) {
    var format = FORMATS[data.format] || FORMATS['10x15'];
    var output = document.createElement('canvas');
    output.width = canvas.width; output.height = canvas.height;
    var ctx = output.getContext('2d');
    var W = 1000, H = W * format.height / format.width;
    ctx.scale(output.width / W, output.height / H);
    useFont(data.font);
    if (!data.album) {
      ctx.fillStyle = '#e7e0d7'; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#756b61'; ctx.font = '500 34px ' + FONT;
      ctx.textAlign = 'center';
      ctx.fillText('L’album de votre histoire', W / 2, H / 2);
      ctx.font = '24px ' + FONT;
      ctx.fillText('Choisissez un album pour découvrir l’affiche', W / 2, H / 2 + 45);
    } else {
      var album = data.album;
      var loaded = await resources(album, data.photo);
      await fontReady(data.font);
      // Set again after the await: another poster may have been drawn meanwhile.
      useFont(data.font);
      var background = posterBackground(loaded[0]);
      // The customer's photo is already cropped square: no sleeve frame to trim.
      var box = data.photo ? { x: 0, y: 0, size: Math.min(loaded[0].naturalWidth, loaded[0].naturalHeight) } : artworkBox(loaded[0]);
      var scale = output.width / W;
      background.paint(ctx, W, H);
      // The sharp artwork (square, frame trimmed, full width) dissolves into the painted
      // background: a vertical alpha mask over its last `fade` part.
      var art = document.createElement('canvas');
      art.width = Math.round(W * scale); art.height = art.width;
      var artCtx = art.getContext('2d');
      artCtx.drawImage(loaded[0], box.x, box.y, box.size, box.size, 0, 0, art.width, art.height);
      var mask = artCtx.createLinearGradient(0, art.height * (1 - background.fade), 0, art.height);
      mask.addColorStop(0, 'rgba(0,0,0,1)'); mask.addColorStop(1, 'rgba(0,0,0,0)');
      artCtx.globalCompositeOperation = 'destination-in';
      artCtx.fillStyle = mask; artCtx.fillRect(0, 0, art.width, art.height);
      ctx.drawImage(art, 0, 0, W, W);
      // Text colour from what is actually painted under the text.
      var rgb = averageArea(output, 0, W * scale, output.width, output.height - W * scale);
      var ink = textColor(rgb);
      ctx.fillStyle = 'rgb(' + ink.join(',') + ')'; ctx.textBaseline = 'top';
      var margin = 60, contentWidth = W - margin * 2;
      var dense = album.tracks.length > 30;
      var heading = fitHeading(ctx, album.title.toLocaleUpperCase('fr'), contentWidth, dense ? 44 : 72, 2);
      var y = W + 15;
      heading.lines.forEach(function (line) { ctx.fillText(line.trim(), margin, y); y += heading.size * 1.04; });
      var artist = fitHeading(ctx, album.artist, contentWidth, dense ? 31 : 38, 2);
      y += 9;
      artist.lines.forEach(function (line) { ctx.fillText(line.trim(), margin, y); y += artist.size * 1.15; });
      y += dense ? 12 : 20;
      var footerY = H - 102;
      var layout = trackLayout(ctx, album.tracks, contentWidth, footerY - 18 - y);
      layout.items.forEach(function (item) {
        ctx.font = '500 ' + item.size + 'px ' + FONT;
        item.lines.forEach(function (line, index) {
          ctx.fillText(line.text, margin + item.x + line.dx, y + item.y + index * item.lineHeight);
        });
      });
      ctx.font = '700 23px ' + FONT;
      ctx.fillText('Date de sortie', margin, footerY);
      ctx.fillText('Durée', margin + 225, footerY);
      ctx.font = '500 23px ' + FONT;
      ctx.fillText(releaseDate(album.releaseDate), margin, footerY + 33);
      ctx.fillText(duration(album.durationMs), margin + 225, footerY + 33);
      // Product barcode first (Deezer UPC), else the original Spotify Code image,
      // never synthesised waveform bars.
      var codeWidth = 310, codeHeight = loaded[1] ? codeWidth * loaded[1].naturalHeight / loaded[1].naturalWidth : 0;
      var colors = palette(loaded[0], 3);
      if (album.barcode) {
        // Small barcode (95 modules × 1.4) above a strip of 3 joined swatches, a little wider,
        // whose bottom lines up with the date and duration values.
        var module = 1.4, stripWidth = 95 * module * 1.2, swatchWidth = stripWidth / 3, stripLeft = W - margin - stripWidth;
        drawBarcode(ctx, album.barcode, W - margin, footerY + 3, 32, module);
        var stripTop = footerY + 47;
        colors.forEach(function (color, index) {
          ctx.fillStyle = 'rgb(' + color.join(',') + ')';
          // Swatches overlap by 0.5 so no background line shows between them.
          ctx.fillRect(stripLeft + index * swatchWidth, stripTop, swatchWidth + (index < 2 ? 0.5 : 0), 9);
        });
        // One thin ink outline around the strip: a swatch matching the background stays visible.
        ctx.strokeStyle = 'rgba(' + ink.join(',') + ',0.45)'; ctx.lineWidth = 1;
        ctx.strokeRect(stripLeft + 0.5, stripTop + 0.5, stripWidth - 1, 8);
        ctx.fillStyle = 'rgb(' + ink.join(',') + ')';
      } else if (loaded[1]) {
        ctx.drawImage(transparentCode(loaded[1], ink), W - margin - codeWidth, footerY - 7, codeWidth, codeHeight);
      } else {
        ctx.font = '500 20px ' + FONT;
        ctx.textAlign = 'right';
        ctx.fillText('sendlove.fr', W - margin, footerY + 29);
        ctx.textAlign = 'left';
      }
    }
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    canvas.getContext('2d').drawImage(output, 0, 0);
    return data.album ? { background: rgb, ink: ink, trackLayout: layout, spotifyCode: !album.barcode && !!loaded[1], barcode: album.barcode || null, palette: colors, footerY: footerY } : null;
  }
  // Canvas exports default to 96 DPI. Write a PNG pHYs chunk for physical 300 DPI printing.
  function printDensity(bytes) {
    var chunk = new Uint8Array(21);
    var view = new DataView(chunk.buffer);
    view.setUint32(0, 9);
    chunk.set([112, 72, 89, 115], 4); // pHYs
    view.setUint32(8, 11811); view.setUint32(12, 11811); chunk[16] = 1;
    var crc = 0xffffffff;
    for (var i = 4; i < 17; i++) {
      crc ^= chunk[i];
      for (var j = 0; j < 8; j++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
    }
    view.setUint32(17, (crc ^ 0xffffffff) >>> 0);
    // Remove an existing density chunk before inserting ours after IHDR.
    var parts = [bytes.slice(0, 33), chunk];
    for (var offset = 33; offset < bytes.length;) {
      var length = new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0) + 12;
      var type = String.fromCharCode.apply(null, bytes.slice(offset + 4, offset + 8));
      if (type !== 'pHYs') parts.push(bytes.slice(offset, offset + length));
      offset += length;
    }
    return parts;
  }
  async function renderSnapshot(block, draft) {
    var data = dataFromDraft(block, draft || {});
    if (!data.album || !draft || !formatValue(draft.Format)) throw new Error('Choisissez un album et un format.');
    var format = FORMATS[data.format];
    var canvas = document.createElement('canvas');
    canvas.width = format.width; canvas.height = format.height;
    await draw(canvas, data);
    var blob = await new Promise(function (resolve) { canvas.toBlob(resolve, 'image/png'); });
    if (!blob) throw new Error('Impossible de préparer le fichier d’impression.');
    var bytes = new Uint8Array(await blob.arrayBuffer());
    return new File(printDensity(bytes), 'affiche-album-' + data.album.id + '-' + data.format + '.png', { type: 'image/png' });
  }
  function node(tag, className, text) {
    var el = document.createElement(tag);
    el.className = className || '';
    if (text) el.textContent = text;
    return el;
  }
  function button(text, className) {
    var el = node('button', className, text); el.type = 'button'; return el;
  }
  function subscribe(ctx, el, handler, cleanup) {
    function changed(event) { if (event.detail && event.detail.handle === ctx.handle) handler(); }
    window.addEventListener('addons:state-changed', changed);
    var observer = new MutationObserver(function () {
      if (!document.body.contains(el)) {
        window.removeEventListener('addons:state-changed', changed);
        observer.disconnect();
        if (cleanup) cleanup();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  function renderPicker(ctx) {
    var key = ctx.block.stateKey || 'Album';
    var restoredFormat = formatValue(ctx.getDraft().Format);
    if (restoredFormat && restoredFormat !== ctx.getDraft().Format) ctx.setDraft('Format', restoredFormat);
    // Two states, laid over the preview zone (block zone 'preview'): searching = a panel
    // covering the empty frame (question, field, results); selected = only a back icon,
    // so the poster, the Validate button and the size choice fit without scrolling.
    var root = node('div', 'album-picker is-searching');
    var panel = node('div', 'album-picker__panel');
    var label = node('label', 'album-picker__label', 'Quel album raconte votre histoire ?');
    var search = node('input', 'album-picker__input');
    search.type = 'search'; search.placeholder = 'Un album, un artiste ou un lien d’album';
    search.id = 'album-search-' + ctx.idx;
    search.autocomplete = 'off'; label.htmlFor = search.id;
    var form = node('form', 'album-picker__search');
    var submit = button('', 'album-picker__submit'); submit.type = 'submit'; submit.setAttribute('aria-label', 'Rechercher');
    submit.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="6.5"/><path d="M16 16l4.5 4.5"/></svg>';
    form.append(search, submit);
    var status = node('p', 'album-picker__status'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    var results = node('div', 'album-picker__results');
    var back = button('', 'album-picker__back'); back.setAttribute('aria-label', 'Changer d’album');
    back.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12H4M10 18l-6-6 6-6"/></svg>';
    var controller = null, timer = null, generation = 0, disposed = false;
    panel.append(label, form, status, results);
    root.append(panel, back);
    // ctx.inline : monté hors submodal (carte attention de la PDP), pas de handle actif.
    function current(g) { return !disposed && g === generation && (ctx.inline || window.FunnelAddons.getHandle() === ctx.handle); }
    function invalidate() {
      generation++;
      clearTimeout(timer);
      if (controller) controller.abort();
      controller = new AbortController();
      return generation;
    }
    function showSelection() {
      var meta = (ctx.getDraft().__meta || {})[key];
      var chosen = !!(meta && meta.album);
      root.classList.toggle('is-selected', chosen);
      root.classList.toggle('is-searching', !chosen);
    }
    // Back to the search: the selection is cleared, the previous results stay listed.
    back.addEventListener('click', function () {
      invalidate(); status.textContent = '';
      ctx.setDraft(key, '', { album: null, ready: false }); showSelection();
      search.focus();
    });
    async function choose(id) {
      var g = invalidate();
      status.textContent = 'Préparation de votre affiche…';
      ctx.setDraft(key, '', { album: null, ready: false }); showSelection();
      try {
        var album = await fetchAlbum(id, controller.signal);
        // Pasted MusicBrainz/Spotify links skip the search check: explain a too small cover.
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(album.id)) await request('/cover?ids=' + encodeURIComponent(album.id), controller.signal);
        var loaded = await resources(album);
        // iTunes covers vary in size: below 1000 px the print would be blurry. (The
        // MusicBrainz proxy already checks the size of its covers.)
        if (browserMode() && loaded[0].naturalWidth < 1000) throw new Error('La pochette de cet album est trop petite pour être imprimée. Choisissez-en un autre.');
        // Verify complete layout and image readability before enabling validation.
        var canvas = document.createElement('canvas'); canvas.width = 760; canvas.height = 1140;
        var renderedFormat = ctx.getDraft().Format || '10x15';
        await draw(canvas, { album: album, format: renderedFormat });
        if (!current(g)) return;
        ctx.setDraft(key, album.title, { album: album, ready: true, renderedFormat: renderedFormat });
        status.textContent = ''; showSelection();
      } catch (error) {
        if (!current(g) || error.name === 'AbortError') return;
        status.textContent = error.message + ' ';
        var retry = button('Réessayer', 'album-picker__retry');
        retry.addEventListener('click', function () { choose(id); });
        status.append(retry);
      }
    }
    async function runSearch() {
      var value = search.value.trim();
      var id = albumId(value);
      if (!id) {
        try {
          var mbURL = new URL(value);
          if (mbURL.protocol === 'https:' && mbURL.hostname === 'musicbrainz.org') id = /^\/release\/([0-9a-f-]{36})\/?$/i.exec(mbURL.pathname)?.[1] || null;
          var deezerId = deezerIdFromURL(value);
          if (deezerId) id = 'deezer-' + deezerId;
        } catch (_) {}
      }
      if (id && browserMode() && !/^(?:deezer|itunes)-/.test(id)) {
        invalidate(); results.replaceChildren();
        status.textContent = 'Tapez le nom de l’album ou de l’artiste, ou collez un lien d’album Deezer.'; return;
      }
      if (id) return choose(id);
      var g = invalidate(); results.replaceChildren();
      if (value.length < 2) { status.textContent = ''; return; }
      if (/^(?:https?:\/\/|spotify:)/i.test(value)) {
        status.textContent = browserMode() ? 'Collez le lien d’un album Deezer (pas d’un titre ou d’une playlist), ou tapez son nom.' : 'Collez le lien d’un album Spotify, Deezer ou d’une édition MusicBrainz (pas d’un titre ou d’une playlist).'; return;
      }
      status.textContent = 'Recherche d’albums…';
      try {
        var data = await searchAlbums(value, controller.signal);
        if (!current(g)) return;
        var albums = (data.albums && data.albums.items || []).filter(function (album) { return album.images && album.images[0] && album.images[0].url; });
        var pendingCovers = albums.length, visibleAlbums = 0;
        status.textContent = albums.length ? 'Chargement des pochettes…' : 'Aucun album avec une pochette disponible. Essayez une autre recherche.';
        albums.forEach(function (album) {
          var item = button('', 'album-picker__result');
          item.hidden = true;
          var image = node('img', ''); image.alt = ''; image.loading = 'eager';
          function coverFinished(available) {
            if (!current(g)) return;
            pendingCovers--;
            if (available) { visibleAlbums++; item.hidden = false; } else item.remove();
            status.textContent = visibleAlbums ? '' : pendingCovers ? 'Chargement des pochettes…' : 'Aucun album avec une pochette disponible. Essayez une autre recherche.';
          }
          image.addEventListener('load', function () { coverFinished(true); }, { once: true });
          image.addEventListener('error', function () { coverFinished(false); }, { once: true });
          if (album.editions && album.editions.length) {
            // The server keeps the best edition whose artwork is large enough to print.
            request('/cover?thumbnail&ids=' + album.editions.map(encodeURIComponent).join(','), controller.signal).then(function (cover) {
              if (!current(g)) return;
              album.id = cover.id;
              image.src = apiURL('/image?url=' + encodeURIComponent(cover.thumbnail));
            }, function () { coverFinished(false); });
          } else {
            try { image.src = imageSource(album.images[0].url); } catch (_) { coverFinished(false); }
          }
          var details = node('span', '');
          details.append(node('strong', '', album.name), node('span', '', (album.artists || []).map(function (a) { return a.name; }).join(', ')), node('small', '', [(album.release_date || '').slice(0, 4), album.total_tracks + ' titres', album.track_hint].filter(Boolean).join(' · ')));
          item.append(image, details); item.addEventListener('click', function () { choose(album.id); });
          results.append(item);
        });
      } catch (error) { if (current(g) && error.name !== 'AbortError') status.textContent = error.message; }
    }
    form.addEventListener('submit', function (event) { event.preventDefault(); runSearch(); });
    search.addEventListener('input', function () {
      invalidate(); results.replaceChildren(); status.textContent = '';
      timer = setTimeout(runSearch, 350);
    });
    showSelection();
    subscribe(ctx, root, showSelection, function () { disposed = true; invalidate(); });
    return root;
  }
  // « Utiliser ma photo » : the customer's photo, cropped square, replaces the artwork
  // (background, colours and palette are then computed from it). Optional.
  var CAMERA_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 8h3l1.6-2.2h6.8L17 8h3v11H4z"/><circle cx="12" cy="13.2" r="3.6"/></svg>';
  function renderPhotoChoice(ctx) {
    var key = ctx.block.stateKey || 'Photo';
    var root = node('div', 'album-photo');
    // Needs the theme's image normaliser (HEIC → JPEG) and crop modal.
    if (!window.ImageNormalize || !window.PhotoCropModal) { root.hidden = true; return root; }
    var input = node('input', ''); input.type = 'file'; input.accept = 'image/*,.heic,.heif'; input.hidden = true;
    var choose = button('', 'album-photo__button');
    var thumb = node('img', 'album-photo__thumb'); thumb.alt = '';
    var text = node('span', '');
    choose.append(thumb, document.createTextNode(''), text);
    choose.insertAdjacentHTML('afterbegin', CAMERA_ICON);
    var reset = button('Remettre la pochette', 'album-photo__reset');
    var status = node('p', 'album-photo__status'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    root.append(choose, reset, status, input);
    var shownFile = null, busy = false;
    function show() {
      var file = ctx.getDraft()[key];
      var has = file instanceof File;
      root.classList.toggle('has-photo', has);
      text.textContent = has ? 'Changer de photo' : 'Utiliser ma photo à la place de la pochette';
      reset.hidden = !has; choose.disabled = busy;
      if (has && file !== shownFile) {
        if (thumb.src) URL.revokeObjectURL(thumb.src);
        thumb.src = URL.createObjectURL(file);
      }
      if (!has && thumb.src) { URL.revokeObjectURL(thumb.src); thumb.removeAttribute('src'); }
      shownFile = has ? file : null;
      // Alerte non bloquante : photo sombre / floue (assets/photo-quality.js).
      if (window.PhotoQuality) { if (has) window.PhotoQuality.check(file, choose); else window.PhotoQuality.clear(choose); }
    }
    choose.addEventListener('click', function () { if (!busy) input.click(); });
    reset.addEventListener('click', function () { status.textContent = ''; ctx.setDraft(key, null); show(); });
    input.addEventListener('change', async function () {
      var file = input.files && input.files[0];
      input.value = '';
      if (!file) return;
      busy = true; show(); status.textContent = 'Préparation de votre photo…';
      try {
        var jpg = await window.ImageNormalize.normalize(file);
        status.textContent = '';
        var cropped = await window.PhotoCropModal.open(jpg, { ratio: 'square' });
        var image = await loadPhoto(cropped);
        // Same print threshold as album covers: 1000 px across the poster width.
        if (Math.min(image.naturalWidth, image.naturalHeight) < 1000) throw new Error('small');
        ctx.setDraft(key, cropped);
      } catch (error) {
        status.textContent = !error || error.message === 'cancelled' ? ''
          : error.message === 'small' ? 'Cette photo est trop petite pour être imprimée (1000 px de côté minimum). Choisissez-en une autre.'
          : 'Cette photo n’a pas pu être traitée, réessayez avec une autre image.';
      }
      busy = false; show();
    });
    show();
    subscribe(ctx, root, show, function () { if (thumb.src) URL.revokeObjectURL(thumb.src); });
    return root;
  }
  function renderPreview(ctx) {
    var root = node('div', 'album-preview');
    var frame = node('div', 'album-preview__frame');
    var canvas = node('canvas', 'album-preview__canvas');
    canvas.setAttribute('role', 'img'); canvas.setAttribute('aria-label', 'Aperçu de votre affiche album encadrée');
    frame.append(canvas);
    var status = node('p', 'album-preview__status'); status.setAttribute('role', 'status');
    // The world (table, shadow, frame) is what the size-step « camera » zooms.
    var world = node('div', 'album-preview__world'); world.append(frame);
    root.append(world, status);
    var generation = 0;
    async function redraw() {
      var g = ++generation;
      var data = dataFromDraft(ctx.block, ctx.getDraft());
      var format = FORMATS[data.format] || FORMATS['10x15'];
      var output = document.createElement('canvas');
      output.width = 760; output.height = Math.round(760 * format.height / format.width);
      status.textContent = data.album ? 'Préparation de l’aperçu…' : '';
      // Album chosen = size step: the framed poster stands on a table, at real scale.
      // Size changes animate only once the scene is shown (no zoom on arrival).
      root.dataset.format = data.format;
      if (data.album && !root.classList.contains('is-staged')) {
        root.classList.add('is-staged');
        requestAnimationFrame(function () { requestAnimationFrame(function () { root.classList.add('is-live'); }); });
      } else if (!data.album) root.classList.remove('is-staged', 'is-live');
      try {
        await draw(output, data);
        if (g !== generation) return;
        canvas.width = output.width; canvas.height = output.height;
        canvas.getContext('2d').drawImage(output, 0, 0);
        // The size cards already say the format: no caption, to save height.
        status.textContent = '';
        if (data.album) {
          var meta = (ctx.getDraft().__meta || {})[ctx.block.stateKey || 'Album'];
          if (meta && (!meta.ready || meta.renderedFormat !== data.format)) ctx.setDraft(ctx.block.stateKey || 'Album', data.album.title, { album: data.album, ready: true, renderedFormat: data.format });
        }
      } catch (error) {
        if (g !== generation) return;
        status.textContent = error.message;
        var meta = (ctx.getDraft().__meta || {})[ctx.block.stateKey || 'Album'];
        if (meta && meta.ready) ctx.setDraft(ctx.block.stateKey || 'Album', data.album.title, { album: data.album, ready: false });
      }
    }
    // A tap on the poster opens it large, drawn at print proportions.
    canvas.addEventListener('click', function () {
      var data = dataFromDraft(ctx.block, ctx.getDraft());
      if (data.album) zoom(data);
    });
    redraw(); subscribe(ctx, root, redraw, function () { generation++; });
    return root;
  }
  function zoom(data) {
    var overlay = node('div', 'album-zoom');
    overlay.setAttribute('role', 'dialog'); overlay.setAttribute('aria-modal', 'true'); overlay.setAttribute('aria-label', 'Affiche en grand');
    var close = button('', 'album-zoom__close'); close.setAttribute('aria-label', 'Fermer');
    close.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    var format = FORMATS[data.format] || FORMATS['10x15'];
    var canvas = node('canvas', 'album-zoom__canvas');
    canvas.width = 1181; canvas.height = Math.round(1181 * format.height / format.width);
    // Same white frame as the preview scene, with a thinner moulding (see .album-zoom__frame).
    var frame = node('div', 'album-preview__frame album-zoom__frame');
    frame.style.setProperty('--poster-w', format.mmWidth / 10);
    frame.style.setProperty('--poster-h', format.mmHeight / 10);
    frame.append(canvas);
    overlay.append(frame, close);
    function dismiss() { overlay.remove(); document.removeEventListener('keydown', onKey); }
    function onKey(event) { if (event.key === 'Escape') dismiss(); }
    overlay.addEventListener('click', dismiss);
    document.addEventListener('keydown', onKey);
    document.body.append(overlay);
    close.focus();
    draw(canvas, data).catch(dismiss);
  }
  window.AlbumPoster = {
    formats: FORMATS, albumId: albumId, normalize: normalize, duration: duration,
    sources: { deezerIdFromURL: deezerIdFromURL, deezerCover: deezerCover, itunesArtwork: itunesArtwork, trustedCover: trustedCover,
      deezerSearchItems: deezerSearchItems, deezerToAlbum: deezerToAlbum, itunesSearchItems: itunesSearchItems, itunesToAlbum: itunesToAlbum },
    formatLabel: formatLabel, textColor: textColor, ean13: ean13, eanModules: eanModules,
    dataFromDraft: dataFromDraft, draw: draw, renderSnapshot: renderSnapshot, fetchAlbum: fetchAlbum,
    cartProperties: function (draft) {
      var data = dataFromDraft({}, draft || {});
      if (!data.album) return {};
      var properties = { Album: data.album.title, Artiste: data.album.artist, Format: formatLabel(data.format), 'Date de sortie': data.album.releaseDate, 'Durée': duration(data.album.durationMs) };
      if (data.album.url) properties['Lien Spotify'] = data.album.url;
      if (data.photo) properties.Pochette = 'Photo du client (à la place de la pochette)';
      return properties;
    }
  };
  function register() {
    if (!window.FunnelAddons) return;
    window.FunnelAddons.registerRenderer('album-picker', {
      render: renderPicker,
      validate: function (block, draft) {
        var meta = (draft.__meta || {})[block.stateKey || 'Album'];
        return !!(meta && meta.ready && meta.album && formatValue(draft.Format) && meta.renderedFormat === formatValue(draft.Format));
      }
    });
    window.FunnelAddons.registerRenderer('album-poster-preview', { render: renderPreview });
    window.FunnelAddons.registerRenderer('album-photo', { render: renderPhotoChoice });
  }
  register();
  document.addEventListener('DOMContentLoaded', register, { once: true });
})();
